/*
 * Delivery check: "can you deliver this to my postcode, and what does it cost?"
 *
 * Asks the store's ShopStar app through Shopify's app proxy (default /apps/shopstar/delivery), which answers with the same rate checkout
 * would offer. If the proxy is not set up or is slow, the shopper sees the plain "shipping is calculated at checkout" message instead of an error.
 * The pure functions at the top are exported for the unit tests in tests/unit.
 */
(() => {
  const POSTCODE_KEY = 'shopstar_postcode';
  const POSTCODE_PATTERN = /^\d{4}$/;

  /** An Australian postcode is exactly four digits. Returns the cleaned postcode or null. */
  const parsePostcode = (value) => {
    const text = String(value ?? '').trim();
    return POSTCODE_PATTERN.test(text) ? text : null;
  };

  /** The proxy's item list: SKU:quantity:unitPriceInCents, comma separated. Lines without a SKU cannot be priced and are left out. */
  const buildItems = (lines) => lines
    .filter((line) => line && String(line.sku || '').trim() !== '' && Number(line.quantity) >= 1)
    .map((line) => `${String(line.sku).trim()}:${Math.min(99, Math.floor(Number(line.quantity)))}:${Math.max(0, Math.round(Number(line.priceCents) || 0))}`)
    .join(',');

  const fill = (template, values) => String(template).replace(/\{(\w+)\}/g, (match, key) => (key in values ? values[key] : match));

  /**
   * What to tell the shopper. `state` is 'ok' | 'unavailable' | 'invalid' | 'error' | 'checking'. Tone drives the colour, never the wording alone.
   */
  const describe = (result, postcode, strings, formatMoney) => {
    switch (result.state) {
      case 'checking': return { tone: 'neutral', text: strings.checking };
      case 'invalid': return { tone: 'warn', text: strings.invalid };
      case 'unavailable': return { tone: 'warn', text: fill(strings.unavailable, { postcode }) };
      case 'ok': {
        const quote = result.quote;
        if (quote.free) return { tone: 'good', text: fill(strings.free, { postcode }) };
        let text = fill(strings.cost, { postcode, cost: formatMoney(quote.cost_cents, quote.currency) });
        if (quote.free_over) text += ' ' + fill(strings.freeOver, { amount: formatMoney(Math.round(quote.free_over * 100), quote.currency) });
        return { tone: 'neutral', text };
      }
      default: return { tone: 'neutral', text: strings.error };
    }
  };

  /** The product page's one-line answer: just the cost, since the postcode is already shown beside "Deliver to". */
  const describeLine = (result, postcode, strings, formatMoney) => {
    if (result.state === 'ok') {
      const quote = result.quote;
      return quote.free
        ? { tone: 'good', text: strings.lineFree }
        : { tone: 'neutral', text: fill(strings.lineCost, { cost: formatMoney(quote.cost_cents, quote.currency) }) };
    }
    if (result.state === 'unavailable') return { tone: 'warn', text: fill(strings.lineUnavailable, { postcode }) };
    return describe(result, postcode, strings, formatMoney);
  };

  /** "Total for 2: $367.90 + $110.00 delivery" once more than one is chosen; nothing for a single item. */
  const totalText = (quantity, priceCents, deliveryCents, strings, formatMoney) => {
    if (!(quantity > 1) || !(priceCents > 0)) return '';
    let text = fill(strings.totalFor, { count: quantity, amount: formatMoney(priceCents * quantity) });
    if (deliveryCents > 0) text += ' ' + fill(strings.plusDelivery, { cost: formatMoney(deliveryCents) });
    return text;
  };

  const defaultMoney = (cents, currency = 'AUD') => {
    try { return new Intl.NumberFormat('en-AU', { style: 'currency', currency }).format(cents / 100); }
    catch (error) { return `$${(cents / 100).toFixed(2)}`; }
  };

  /** One round trip to the proxy. Never throws: every failure becomes { state: 'error' }. */
  const requestQuote = async (path, postcode, items, fetchImpl, timeoutMs = 8000) => {
    const controller = typeof AbortController !== 'undefined' ? new AbortController() : null;
    const timer = controller ? setTimeout(() => controller.abort(), timeoutMs) : null;
    try {
      const url = `${path}?postcode=${encodeURIComponent(postcode)}&items=${encodeURIComponent(items)}`;
      const response = await fetchImpl(url, { headers: { Accept: 'application/json' }, signal: controller?.signal });
      if (response.status === 400) return { state: 'invalid' };
      if (!response.ok) return { state: 'error' };
      const quote = await response.json();
      if (!quote || quote.ok !== true) return { state: quote && quote.reason === 'invalid_postcode' ? 'invalid' : quote && quote.reason === 'unavailable' ? 'unavailable' : 'error' };
      return quote.available ? { state: 'ok', quote } : { state: 'unavailable' };
    } catch (error) {
      return { state: 'error' };
    } finally {
      if (timer) clearTimeout(timer);
    }
  };

  const api = { parsePostcode, buildItems, describe, describeLine, totalText, requestQuote, fill };
  if (typeof module !== 'undefined' && module.exports) module.exports = api;
  if (typeof window === 'undefined') return;
  window.ShopStarDelivery = api;

  const readStored = () => { try { return window.localStorage.getItem(POSTCODE_KEY) || ''; } catch (error) { return ''; } };
  const writeStored = (value) => { try { window.localStorage.setItem(POSTCODE_KEY, value); } catch (error) { /* private mode */ } };

  /** The cart lines this form covers: the page's own product (and chosen quantity/variant), or every line of the cart. */
  const linesFor = (root) => {
    if (root.dataset.deliveryMode === 'cart') {
      return [...root.querySelectorAll('[data-delivery-line]')].map((line) => ({
        sku: line.dataset.sku, quantity: Number(line.dataset.quantity), priceCents: Number(line.dataset.priceCents)
      }));
    }
    const scope = root.closest('[data-product-form-scope]') || document;
    const quantity = Number(scope.querySelector('input[name="quantity"]')?.value || 1);
    const variantId = scope.querySelector('[data-variant-input]')?.value;
    let sku = root.dataset.sku;
    let priceCents = Number(root.dataset.priceCents);
    try {
      const product = JSON.parse(scope.querySelector('[data-product-json]')?.textContent || 'null');
      const variant = product?.variants?.find((item) => String(item.id) === String(variantId));
      if (variant) { sku = variant.sku; priceCents = Number(variant.price); }
    } catch (error) { /* fall back to the values rendered with the page */ }
    return [{ sku, quantity, priceCents }];
  };

  const init = () => {
    const strings = window.themeStrings?.delivery;
    if (!strings) return;
    document.querySelectorAll('[data-delivery-estimator]').forEach((form) => {
      if (form.dataset.deliveryReady) return;   // already wired (this runs again when the cart page is redrawn)
      form.dataset.deliveryReady = '1';
      const root = form.closest('.delivery-estimator');
      const output = root?.querySelector('[data-delivery-output]');
      const input = form.querySelector('input[name="postcode"]');
      if (!root || !output || !input) return;
      if (!input.value) input.value = readStored();
      const auto = !!input.value;
      const line = root.classList.contains('delivery-card--line');
      const scope = root.closest('[data-product-form-scope]');
      const postcodeText = root.querySelector('[data-delivery-postcode]');
      const changeButton = root.querySelector('[data-delivery-change]');
      const totalNode = scope?.querySelector('[data-product-total]');
      let deliveryCents = 0;
      const money = (cents, currency) => defaultMoney(cents, currency);
      const show = (result, postcode) => {
        const { tone, text } = (line ? describeLine : describe)(result, postcode, strings, money);
        output.textContent = text;
        output.dataset.tone = tone;
      };
      // On the product page a known postcode collapses to "Deliver to 3131  Change"; Change brings the box back.
      const collapse = (postcode) => {
        if (!line || !postcodeText || !changeButton) return;
        postcodeText.textContent = postcode;
        postcodeText.hidden = false; changeButton.hidden = false; form.hidden = true;
      };
      const edit = () => {
        if (postcodeText) postcodeText.hidden = true;
        if (changeButton) changeButton.hidden = true;
        form.hidden = false; input.focus(); input.select();
      };
      const updateTotal = () => {
        if (!totalNode || !line) return;
        const [item] = linesFor(root);
        totalNode.textContent = item ? totalText(item.quantity, item.priceCents, deliveryCents, strings, money) : '';
        totalNode.hidden = totalNode.textContent === '';
      };
      const run = async (quiet) => {
        const postcode = parsePostcode(input.value);
        if (!postcode) { if (!quiet) { show({ state: 'invalid' }, ''); input.focus(); } return; }
        const items = buildItems(linesFor(root));
        if (!items) { show({ state: 'error' }, postcode); return; }
        writeStored(postcode);
        const button = form.querySelector('[type="submit"], [data-delivery-submit]');
        button?.setAttribute('aria-busy', 'true');
        show({ state: 'checking' }, postcode);
        const result = await requestQuote(root.dataset.proxyPath || '/apps/shopstar/delivery', postcode, items, window.fetch.bind(window));
        show(result, postcode);
        deliveryCents = result.state === 'ok' && !result.quote.free ? Number(result.quote.cost_cents) || 0 : 0;
        if (result.state === 'ok' || result.state === 'unavailable') collapse(postcode);
        updateTotal();
        button?.removeAttribute('aria-busy');
        window.dispatchEvent(new CustomEvent('shopstar:delivery_checked', { detail: { postcode, result, root } }));
      };
      if (form.tagName === 'FORM') form.addEventListener('submit', (event) => { event.preventDefault(); run(false); });
      if (line) {
        // The product page has no Check button to press: the answer comes as soon as four digits are typed.
        input.addEventListener('input', () => { if (parsePostcode(input.value)) run(false); });
        // Clicking away from the box (change fires on leaving it) also asks: a pasted or autofilled postcode, or one that is not four digits yet.
        input.addEventListener('change', () => {
          if (parsePostcode(input.value)) run(false);
          else if (input.value.trim() !== '') show({ state: 'invalid' }, '');
        });
        changeButton?.addEventListener('click', edit);
        // A different quantity or option changes the total and what delivery costs: ask again for the new basket.
        if (scope) {
          let timer = 0;
          const refresh = () => {
            clearTimeout(timer);
            timer = setTimeout(() => { updateTotal(); if (parsePostcode(input.value)) run(true); }, 250);
          };
          const onQuantity = (event) => { if (event.target.closest?.('[data-product-quantity-input]')) refresh(); };
          scope.addEventListener('change', onQuantity);
          scope.addEventListener('input', onQuantity);
          scope.addEventListener('shopstar:variant_change', refresh);
        }
      } else {
        // Inside another form (the cart's), so not a form itself: the button and the Enter key do the same job.
        form.querySelector('[data-delivery-submit]')?.addEventListener('click', () => run(false));
        input.addEventListener('keydown', (event) => { if (event.key === 'Enter') { event.preventDefault(); run(false); } });
      }
      // A shopper who has checked before sees the cost straight away.
      if (auto) run(true);
    });
  };

  /** On the cart page: a Delivery line and a total with delivery appear under the order summary once a cost is known. */
  const updateCartSummary = (event) => {
    const { postcode, result, root } = event.detail || {};
    if (!root || root.dataset.deliveryMode !== 'cart') return;
    const row = document.querySelector('[data-cart-delivery-row]');
    const totalRow = document.querySelector('[data-cart-delivery-total-row]');
    if (!row || !totalRow) return;
    if (result?.state !== 'ok') { row.hidden = true; totalRow.hidden = true; return; }
    const quote = result.quote;
    const total = document.querySelector('[data-cart-page-total]');
    row.hidden = false;
    row.querySelector('[data-cart-delivery-postcode]').textContent = '(' + postcode + ')';
    row.querySelector('[data-cart-delivery-amount]').textContent = quote.free ? 'Free' : defaultMoney(quote.cost_cents, quote.currency);
    const base = total ? Number(String(total.textContent).replace(/[^0-9.]/g, '')) : NaN;
    if (Number.isFinite(base)) {
      totalRow.hidden = false;
      document.querySelector('[data-cart-delivery-total]').textContent = defaultMoney(Math.round(base * 100) + (quote.free ? 0 : quote.cost_cents), quote.currency);
    }
  };
  window.addEventListener('shopstar:delivery_checked', updateCartSummary);

  // The cart page is redrawn after every change (a quantity, removing a line): the new card needs wiring, and its cost re-checking for what is left in the cart.
  document.addEventListener('shopstar:cart_page_replaced', init);

  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', init);
  else init();
})();
